// Firebase functions for video management and reflections
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp
} from "firebase/firestore";
import { db } from "./firebase-config";
import { saveReflectionVideoToStorage, deleteReflectionVideoFromStorage } from "./storage";

// Get user's reflection videos
export const getUserReflections = async (userId) => {
  try {
    const userReflectionsRef = doc(db, "user_reflections", userId);
    const docSnap = await getDoc(userReflectionsRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        success: true,
        data: data.reflections || []
      };
    }
    
    return {
      success: true,
      data: []
    };
  } catch (error) {
    console.error("Error getting user reflections:", error);
    return { success: false, error: error.message };
  }
};

// Save reflection video to Firebase
export const saveReflectionVideo = async (userId, videoBlob, questionIndex, questionText) => {
  try {
    // Upload video to storage first
    const uploadResult = await saveReflectionVideoToStorage(userId, videoBlob, questionIndex, questionText);
    
    if (!uploadResult.success) {
      return uploadResult;
    }
    
    // Save metadata to Firestore
    const userReflectionsRef = doc(db, "user_reflections", userId);
    const docSnap = await getDoc(userReflectionsRef);
    
    const reflectionData = {
      questionIndex,
      questionText,
      videoUrl: uploadResult.videoUrl,
      storagePath: uploadResult.storagePath,
      timestamp: uploadResult.timestamp
    };
    
    if (docSnap.exists()) {
      // Update existing reflections document
      // Get current reflections
      const currentReflections = docSnap.data().reflections || [];
      
      // Update or add the new reflection at the correct index
      while (currentReflections.length <= questionIndex) {
        currentReflections.push(null);
      }
      currentReflections[questionIndex] = reflectionData;
      
      // Update the document
      await updateDoc(userReflectionsRef, {
        reflections: currentReflections,
        lastUpdated: serverTimestamp()
      });
    } else {
      // Create new reflections document
      const reflectionsArray = Array(questionIndex + 1).fill(null);
      reflectionsArray[questionIndex] = reflectionData;
      
      await setDoc(userReflectionsRef, {
        userId,
        reflections: reflectionsArray,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
    }
    
    return {
      success: true,
      videoUrl: uploadResult.videoUrl,
      storagePath: uploadResult.storagePath,
      timestamp: uploadResult.timestamp
    };
  } catch (error) {
    console.error("Error saving reflection video:", error);
    return { success: false, error: error.message };
  }
};

// Delete a reflection video
export const deleteReflectionVideo = async (userId, questionIndex, storagePath) => {
  try {
    // Delete from storage if path is provided
    if (storagePath) {
      await deleteReflectionVideoFromStorage(storagePath);
    }
    
    // Update Firestore document
    const userReflectionsRef = doc(db, "user_reflections", userId);
    const docSnap = await getDoc(userReflectionsRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      const reflections = data.reflections || [];
      
      if (reflections[questionIndex]) {
        // Set this reflection to null instead of removing it
        // This preserves the array indices that correspond to question indices
        reflections[questionIndex] = null;
        
        await updateDoc(userReflectionsRef, {
          reflections: reflections,
          lastUpdated: serverTimestamp()
        });
      }
    }
    
    return { success: true };
  } catch (error) {
    console.error("Error deleting reflection video:", error);
    return { success: false, error: error.message };
  }
};